import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  Play,
  Brain,
  Sparkles,
  Clock,
  TrendingUp,
  Eye,
  Zap,
  Target,
  Lightbulb,
  Users,
  Gamepad2,
  Timer,
  Star,
  CheckCircle,
  BookOpen,
} from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';

const LANGUAGES = [
  { code: 'es', name: 'Español', flag: 'CR' },
  { code: 'en', name: 'English', flag: 'GB' },
];

const content = {
  es: {
    nav: {
      why: '¿Por qué?',
      skills: 'Habilidades',
      stats: 'Estadísticas',
    },
    hero: {
      badge: 'Entrena tu cerebro cada día',
      title: 'Juegos cerebrales',
      titleHighlight: 'para todas las habilidades cognitivas',
      subtitle:
        'Junto con neurocientíficos, transformamos ejercicios basados en la ciencia en juegos divertidos y desafiantes. Solo 10 minutos al día para mantener tu cerebro en forma.',
      cta: 'Empezar juego cerebral',
      trustLine: 'Más de 300.000 usuarios entrenan su cerebro con nosotros',
    },
    why: {
      title: '¿Por qué BrainGymnastics?',
      subtitle: 'Ciencia, diversión y resultados en tu vida diaria',
      items: [
        {
          icon: 'sparkles',
          tag: 'De la ciencia al divertimento',
          title: 'Convertimos la ciencia en diversión',
          desc: 'Junto con neurocientíficos, nuestro equipo transforma ejercicios basados en la ciencia en juegos divertidos y desafiantes para el cerebro.',
        },
        {
          icon: 'clock',
          tag: '10 minutos al día',
          title: 'Entrena tu cerebro sin esfuerzo',
          desc: 'Diez minutos al día es todo lo que necesitas para mantener tu cerebro en forma, ¡igual que el deporte fortalece tu cuerpo!',
        },
        {
          icon: 'trending',
          tag: 'Beneficio diario',
          title: 'Nota el efecto en la vida cotidiana',
          desc: 'Mejora tus habilidades cognitivas del día a día como reconocimiento facial, concentración, matemáticas, memoria a corto plazo y mucho más.',
        },
      ],
    },
    skills: {
      badge: 'Cinco habilidades cognitivas',
      title: 'Juegos de entrenamiento cerebral para todas las habilidades cognitivas',
      subtitle:
        'Tu cerebro tiene un enorme rango de habilidades, que se pueden dividir en cinco habilidades cognitivas principales. Nuestros juegos cerebrales te desafían a ejercitar estas habilidades.',
      footer:
        'Todos los juegos cerebrales se basan en tareas y pruebas psicológicas confiables. Usa nuestros juegos cerebrales gratuitos para mejorar tu memoria, atención, velocidad de pensamiento, percepción y razonamiento lógico.',
      explore: 'Jugar ahora',
      items: [
        { icon: 'book', title: 'Memoria', desc: 'Fortalece tu memoria a corto y largo plazo con ejercicios probados.' },
        { icon: 'target', title: 'Atención', desc: 'Mejora tu concentración y capacidad de mantener el foco.' },
        { icon: 'zap', title: 'Velocidad de pensamiento', desc: 'Procesa información más rápido y toma decisiones ágiles.' },
        { icon: 'eye', title: 'Percepción', desc: 'Afina tu reconocimiento visual y percepción espacial.' },
        { icon: 'lightbulb', title: 'Razonamiento lógico', desc: 'Desarrolla tu pensamiento analítico y resolución de problemas.' },
      ],
    },
    stats: [
      { value: '300 000+', label: 'Usuarios' },
      { value: '1 400 000+', label: 'Juegos jugados' },
      { value: '27 000+', label: 'Horas entrenadas' },
    ],
    faq: {
      title: 'Lo que la gente nos pregunta a menudo',
      items: [
        {
          q: '¿Qué es el entrenamiento cerebral?',
          a: 'El entrenamiento cerebral es el uso de ejercicios digitales, también llamados juegos cerebrales. Estos ejercicios se utilizan para estimular la actividad mental con el propósito de mejorar tus habilidades cognitivas.',
        },
        {
          q: '¿Realmente funcionan los juegos cerebrales?',
          a: 'Los juegos cerebrales son una ciencia muy nueva y muchos investigadores aún están descubriendo sus efectos. Mientras que decenas de millones de personas en todo el mundo usan juegos cerebrales, los resultados científicos aún están en proceso de ser descubiertos. Actualmente apoyamos a diversas universidades internacionales en sus estudios.',
        },
      ],
    },
    cta: {
      title: 'Solo tienes un cerebro, cuídalo bien.',
      subtitle: 'Empieza hoy con solo 10 minutos al día. Juegos gratuitos, basados en ciencia, disponibles en tu móvil.',
      button: 'Empezar juego cerebral',
    },
    footer: {
      rights: 'Todos los derechos reservados.',
      tagline: 'Juegos cerebrales gratuitos basados en ciencia.',
      disclaimer:
        'Este servicio es único y exclusivo para usuarios ICE. Brain Gymnastics es una comunidad por suscripción del 1416. El usuario recibirá un mensaje diario con un precio de 228 colones IVAI y el cobró será automático a su facturación celular o bien descargado del saldo principal de su cuenta. Para solicitar la desafiliación del servicio deberá de enviar la palabra BAJA ó SALIR al código 1416. La suscripción será indefinida y se mantendrá vigente mientras el usuario no la cancele. El usuario será responsable de verificar compatibilidad con su terminal. No aplican devoluciones ni reembolsos. Servicio proporcionado por Silver Solutions. Para atención al cliente dirigirse al 22710290.',
    },
  },
  en: {
    nav: {
      why: 'Why?',
      skills: 'Skills',
      stats: 'Stats',
    },
    hero: {
      badge: 'Train your brain every day',
      title: 'Brain training games',
      titleHighlight: 'for all cognitive skills',
      subtitle:
        'Together with neuroscientists, we transform science-based exercises into fun and challenging games. Just 10 minutes a day to keep your brain in shape.',
      cta: 'Start brain game',
      trustLine: 'Over 300,000 users train their brain with us',
    },
    why: {
      title: 'Why BrainGymnastics?',
      subtitle: 'Science, fun, and real benefits in everyday life',
      items: [
        {
          icon: 'sparkles',
          tag: 'Science to fun',
          title: 'Turning science into fun',
          desc: 'Together with neuroscientists, our team transforms science-based exercises into fun and challenging games for the brain!',
        },
        {
          icon: 'clock',
          tag: '10 minutes a day',
          title: 'Training your brain without any effort',
          desc: 'Ten minutes a day is all it takes to keep your brain in shape, just like sports strengthens your body!',
        },
        {
          icon: 'trending',
          tag: 'Benefit daily',
          title: 'Notice the effect in everyday life',
          desc: 'Improve your day-to-day cognitive skills like facial recognition, concentration, math, short-term memory and much more!',
        },
      ],
    },
    skills: {
      badge: 'Five cognitive skills',
      title: 'Brain training games for all cognitive skills',
      subtitle:
        'Your brain has an enormous range of abilities, which can be divided in five major cognitive skills. Our brain games challenge you to exercise these skills.',
      footer:
        'All brain games are based on trusted psychological tasks and tests. Use our free brain games to improve your memory, attention, thinking speed, perception and logical reasoning!',
      explore: 'Play now',
      items: [
        { icon: 'book', title: 'Memory', desc: 'Strengthen short- and long-term memory with proven exercises.' },
        { icon: 'target', title: 'Attention', desc: 'Improve your concentration and ability to stay focused.' },
        { icon: 'zap', title: 'Thinking speed', desc: 'Process information faster and make quicker decisions.' },
        { icon: 'eye', title: 'Perception', desc: 'Sharpen visual recognition and spatial perception.' },
        { icon: 'lightbulb', title: 'Logical reasoning', desc: 'Develop analytical thinking and problem-solving skills.' },
      ],
    },
    stats: [
      { value: '300,000+', label: 'Users' },
      { value: '1,400,000+', label: 'Games played' },
      { value: '27,000+', label: 'Hours trained' },
    ],
    faq: {
      title: 'What people often ask us',
      items: [
        {
          q: 'What is brain training?',
          a: 'Brain training is the usage of digital exercises, also called brain games. Those exercises are used to stimulate mental activities with the purpose of improving your cognitive abilities.',
        },
        {
          q: 'Do brain games really work?',
          a: 'Brain games are a very new science, and many researchers are still discovering the effects. While tens of millions of people worldwide are using brain games, scientific results are still very much in the process of being discovered. Currently we support a variety of international universities in their studies.',
        },
      ],
    },
    cta: {
      title: 'You only have one brain, take good care of it.',
      subtitle: 'Start today with just 10 minutes a day. Free, science-based games available on your phone.',
      button: 'Start brain game',
    },
    footer: {
      rights: 'All rights reserved.',
      tagline: 'Free science-based brain training games.',
      disclaimer:
        'This service is exclusive to ICE users. Brain Gymnastics is a subscription community on short code 1416. Users will receive a daily message priced at 228 colones IVAI, charged automatically to their mobile bill or deducted from their main account balance. To unsubscribe, send BAJA or SALIR to 1416. The subscription is indefinite and remains active until cancelled by the user. Users are responsible for verifying compatibility with their device. No refunds apply. Service provided by Silver Solutions. Customer support: 22710290.',
    },
  },
};

const skillIcons = {
  book: BookOpen,
  target: Target,
  zap: Zap,
  eye: Eye,
  lightbulb: Lightbulb,
};

const whyIcons = {
  sparkles: Sparkles,
  clock: Clock,
  trending: TrendingUp,
};

const skillColors = [
  'from-violet-500 to-purple-700',
  'from-blue-500 to-indigo-700',
  'from-cyan-500 to-teal-600',
  'from-fuchsia-500 to-pink-600',
  'from-amber-500 to-orange-600',
];

const skillBg = [
  'from-violet-50 to-purple-100',
  'from-blue-50 to-indigo-100',
  'from-cyan-50 to-teal-50',
  'from-fuchsia-50 to-pink-50',
  'from-amber-50 to-orange-50',
];

const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=1600&q=80',
  skills: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=1200&q=80',
  cta: 'https://images.unsplash.com/photo-1509228627152-c72ed5285919?w=1600&q=80',
};

function App() {
  const [lang, setLang] = useState('es');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const dropdownRef = useRef(null);

  const t = content[lang];

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLang = LANGUAGES.find((l) => l.code === lang);

  return (
    <div className="min-h-screen bg-white font-body text-dark">

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center space-x-2">
              <div className="w-9 h-9 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center shadow-sm">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-dark">
                24<span className="text-primary">braingym</span>
              </span>
            </a>

            <nav className="hidden md:flex items-center space-x-8">
              <a href="#why" className="text-dark/70 hover:text-primary transition-colors font-medium text-sm">{t.nav.why}</a>
              <a href="#skills" className="text-dark/70 hover:text-primary transition-colors font-medium text-sm">{t.nav.skills}</a>
              <a href="#stats" className="text-dark/70 hover:text-primary transition-colors font-medium text-sm">{t.nav.stats}</a>
            </nav>

            <div className="flex items-center space-x-3">
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-200 hover:border-primary/40 transition-colors text-sm bg-white"
                >
                  <ReactCountryFlag countryCode={currentLang.flag} svg style={{ width: '18px', height: '14px' }} />
                  <span className="font-medium hidden sm:inline">{currentLang.code.toUpperCase()}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-muted" />
                </button>
                {langDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-50">
                    {LANGUAGES.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => { setLang(l.code); setLangDropdownOpen(false); }}
                        className={`w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-light transition-colors text-sm ${
                          lang === l.code ? 'text-primary font-semibold bg-primary/5' : 'text-dark'
                        }`}
                      >
                        <ReactCountryFlag countryCode={l.flag} svg style={{ width: '20px', height: '15px' }} />
                        <span>{l.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="/start"
                className="hidden sm:inline-flex items-center bg-primary text-white px-5 py-2 rounded-xl hover:bg-primary-dark transition-colors font-semibold text-sm shadow-sm"
              >
                {t.hero.cta}
              </a>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-dark hover:bg-gray-100 transition-colors"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-3 space-y-1">
              {[
                { href: '#why', label: t.nav.why },
                { href: '#skills', label: t.nav.skills },
                { href: '#stats', label: t.nav.stats },
              ].map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2.5 text-dark hover:text-primary transition-colors font-medium rounded-lg hover:bg-light"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="/start"
                className="block text-center bg-primary text-white px-4 py-3 rounded-xl font-semibold mt-2"
              >
                {t.hero.cta}
              </a>
            </div>
          </div>
        )}
      </header>

      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-950 via-violet-950 to-slate-900" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url('${IMAGES.hero}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-indigo-950/90 via-transparent to-indigo-950/30" />

        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-secondary/20 rounded-full blur-3xl animate-pulse-slow" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-32 md:py-40">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 bg-primary/20 border border-primary/30 text-violet-300 px-4 py-1.5 rounded-full text-sm font-semibold mb-6 backdrop-blur-sm">
              <Brain className="w-4 h-4" />
              <span>{t.hero.badge}</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 font-heading leading-tight">
              {t.hero.title}
              <br />
              <span className="bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
                {t.hero.titleHighlight}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-slate-300 mb-10 leading-relaxed max-w-2xl">
              {t.hero.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row items-start gap-4">
              <a
                href="/start"
                className="group inline-flex items-center bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-2xl text-lg font-bold transition-all shadow-2xl shadow-primary/40 hover:shadow-primary/60 hover:scale-105"
              >
                <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                {t.hero.cta}
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>

            <div className="flex items-center space-x-3 mt-8">
              <div className="flex -space-x-2">
                {['🧠', '🎯', '⚡', '🧩'].map((emoji, i) => (
                  <span
                    key={i}
                    className="w-8 h-8 rounded-full bg-white/10 border-2 border-white/20 flex items-center justify-center text-sm backdrop-blur-sm"
                  >
                    {emoji}
                  </span>
                ))}
              </div>
              <span className="text-slate-400 text-sm">{t.hero.trustLine}</span>
            </div>
          </div>
        </div>
      </section>

      <section id="why" className="py-24 bg-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
              <Sparkles className="w-4 h-4" />
              <span>BrainGymnastics</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.why.title}</h2>
            <p className="text-muted text-lg max-w-2xl mx-auto">{t.why.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {t.why.items.map((item, i) => {
              const Icon = whyIcons[item.icon];
              return (
                <div
                  key={i}
                  className="bg-white rounded-2xl p-8 border border-violet-100 hover:border-primary/20 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center mb-5 shadow-lg">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-primary">{item.tag}</span>
                  <h3 className="font-heading font-bold text-dark text-xl mt-2 mb-3">{item.title}</h3>
                  <p className="text-muted text-sm leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="skills" className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-secondary" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
            <div>
              <div className="inline-flex items-center space-x-2 bg-accent/10 text-primary px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                <Gamepad2 className="w-4 h-4" />
                <span>{t.skills.badge}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.skills.title}</h2>
              <p className="text-muted text-lg leading-relaxed">{t.skills.subtitle}</p>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={IMAGES.skills}
                alt=""
                className="w-full h-64 lg:h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-indigo-950/40 to-transparent" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {t.skills.items.map((skill, i) => {
              const Icon = skillIcons[skill.icon];
              return (
                <a
                  key={i}
                  href="/start"
                  className={`group relative bg-gradient-to-br ${skillBg[i]} rounded-2xl p-6 border border-white hover:border-primary/20 hover:shadow-xl transition-all duration-300 overflow-hidden`}
                >
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${skillColors[i]} flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-dark text-lg mb-2">{skill.title}</h3>
                  <p className="text-muted text-sm leading-relaxed mb-4">{skill.desc}</p>
                  <span className="inline-flex items-center text-sm font-semibold text-primary group-hover:translate-x-1 transition-transform">
                    {t.skills.explore} <ChevronRight className="w-4 h-4 ml-1" />
                  </span>
                </a>
              );
            })}
          </div>

          <div className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-2xl p-8 border border-violet-100 flex flex-col md:flex-row items-center gap-6">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-primary" />
            </div>
            <p className="text-muted leading-relaxed flex-1">{t.skills.footer}</p>
            <a
              href="/start"
              className="inline-flex items-center bg-primary text-white px-6 py-3 rounded-xl font-bold hover:bg-primary-dark transition-all shadow-lg shadow-primary/30 hover:scale-105 flex-shrink-0"
            >
              {t.hero.cta}
              <ChevronRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      <section id="stats" className="py-20 bg-gradient-to-br from-indigo-950 via-violet-950 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 border-2 border-white rounded-full" />
          <div className="absolute bottom-10 right-10 w-60 h-60 border-2 border-white rounded-full" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {t.stats.map((stat, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-10 backdrop-blur-sm hover:bg-white/10 transition-colors">
                <div className="flex justify-center mb-4">
                  {i === 0 && <Users className="w-8 h-8 text-violet-400" />}
                  {i === 1 && <Gamepad2 className="w-8 h-8 text-cyan-400" />}
                  {i === 2 && <Timer className="w-8 h-8 text-amber-400" />}
                </div>
                <div className="text-4xl md:text-5xl font-bold text-white font-heading mb-2">{stat.value}</div>
                <div className="text-slate-400 text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <a
              href="/start"
              className="inline-flex items-center bg-primary text-white px-8 py-4 rounded-2xl text-lg font-bold hover:bg-primary-dark transition-all shadow-xl shadow-primary/30 hover:shadow-primary/50 hover:scale-105"
            >
              <Play className="w-5 h-5 mr-2" />
              {t.hero.cta}
              <ChevronRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      <section id="faq" className="py-24 bg-light">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-secondary/10 text-secondary-dark px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
              <Star className="w-4 h-4" />
              <span>FAQ</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.faq.title}</h2>
          </div>
          <div className="space-y-3">
            {t.faq.items.map((item, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-dark pr-4 leading-snug">{item.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-muted flex-shrink-0 transition-transform duration-200 ${
                      openFaq === i ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 border-t border-gray-50">
                    <p className="text-muted leading-relaxed pt-4">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-br from-primary to-primary-dark relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url('${IMAGES.cta}')` }}
        />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 border-2 border-white rounded-full" />
          <div className="absolute bottom-10 right-10 w-60 h-60 border-2 border-white rounded-full" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Brain className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 font-heading">{t.cta.title}</h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">{t.cta.subtitle}</p>
          <a
            href="/start"
            className="inline-flex items-center bg-white text-primary px-8 py-4 rounded-2xl text-lg font-bold hover:bg-gray-50 transition-all shadow-2xl hover:scale-105"
          >
            <Play className="w-5 h-5 mr-2" />
            {t.cta.button}
            <ChevronRight className="ml-2 w-5 h-5" />
          </a>
        </div>
      </section>

      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center">
                <Brain className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading font-bold text-lg">
                24<span className="text-violet-400">braingym</span>
              </span>
            </div>
            <p className="text-slate-500 text-sm mb-6">{t.footer.tagline}</p>
            <p className="text-slate-600 text-sm mb-8">
              © {new Date().getFullYear()} 24braingym.com – {t.footer.rights}
            </p>
            <div className="border-t border-slate-800 pt-8 mt-4">
              <div className="max-w-4xl mx-auto">
                <p className="text-xs text-slate-500 leading-relaxed">
                  {t.footer.disclaimer}
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
